import axios from "axios";
import moment from "moment";
import {
  AUDIT_TRAIL_PARAMS_NAMES,
  DateFormat,
  studiesIds,
} from "../../../modules/constants";
import { handleHttpErrors } from "../../../modules/http";

// Constants
const SET_EVENTS = "SET_EVENTS";
const SET_FILTER_BEFORE = "SET_FILTER_BEFORE";
const SET_FILTER_AFTER = "SET_FILTER_AFTER";
const SET_FILTER_ACTOR = "SET_FILTER_ACTOR";
const SET_FILTER_ACTOR_TYPE = "SET_FILTER_ACTOR_TYPE";
const SET_FILTER_SUBJECT_ACCOUNT = "SET_FILTER_SUBJECT_ACCOUNT";
const SET_FILTER_ACTION = "SET_FILTER_ACTION";
const SET_FILTER_STATUS = "SET_FILTER_STATUS";
const SET_FILTER_SUBJECT_SITE = "SET_FILTER_SUBJECT_SITE";
const SET_FILTER_RESOURCE = "SET_FILTER_RESOURCE";
const SET_DICTIONARIES = "SET_DICTIONARIES";
const RESET_EVENTS = "RESET_EVENTS";
const RESET_FILTERS = "RESET_FILTERS";
const OVERRIDE_FILTERS = "OVERRIDE_FILTERS";
const LOGOUT = "LOGOUT";
const SET_AVAILABLE_SITES = "SET_AVAILABLE_SITES";
const UPDATE_EVENTS = "UPDATE_EVENTS";
// Acessors
const getEvents = (state) => state?.events;
const getFilterBefore = (state) => state?.filterBefore;
const getFilterAfter = (state) => state?.filterAfter;
const getFilterActor = (state) => state?.filterActor;
const getFilterActorType = (state) => state?.filterActorType;
const getFilterSubjectAccount = (state) => state?.filterSubjectAccount;
const getFilterAction = (state) => state?.filterAction;
const getFilterStatus = (state) => state?.filterStatus;
const getFilterSubjectSite = (state) => state?.filterSubjectSite;
const getFilterResource = (state) => state?.filterResource;
const getActorTypes = (state) => state?.actorTypes;
const getActions = (state) => state?.actions;
const getStatuses = (state) => state?.statuses;
const getSites = (state) => state?.sites;
const getFilterTags = (state) => state?.filterTags;
// Default state
const initialState = {
  events: [],
  filterBefore: null,
  filterAfter: null,
  filterActor: null,
  filterActorType: null,
  filterSubjectAccount: null,
  filterAction: null,
  filterStatus: null,
  filterSubjectSite: null,
  filterResource: null,
  actorTypes: [],
  actions: [],
  statuses: [],
  sites: [],
  canFetchInitialEvents: false,
  filterTags: {},
};

export const auditTrailReducer = (state = initialState, action) => {
  switch (action.type) {
    case SET_EVENTS:
      const { data } = action;
      return {
        ...state,
        events: state.events
          .concat(data)
          .filter((v, i, a) => a.findIndex((v2) => v2.id === v.id) === i),
      };
    case UPDATE_EVENTS:
      const { events } = action;
      return {
        ...state,
        events: events,
      };
    case SET_FILTER_BEFORE:
      const { filterBefore } = action;
      return {
        ...state,
        filterBefore: filterBefore,
        filterTags: {
          ...state.filterTags,
          filterBefore: filterBefore
            ? {
                id: AUDIT_TRAIL_PARAMS_NAMES.before,
                title: "To:",
                text: formatDateTimeFilter(filterBefore),
                value: filterBefore,
              }
            : null,
        },
      };
    case SET_FILTER_AFTER:
      const { filterAfter } = action;
      return {
        ...state,
        filterAfter: filterAfter,
        filterTags: {
          ...state.filterTags,
          filterAfter: filterAfter
            ? {
                id: AUDIT_TRAIL_PARAMS_NAMES.after,
                title: "From:",
                text: formatDateTimeFilter(filterAfter),
                value: filterAfter,
              }
            : null,
        },
      };
    case SET_FILTER_ACTOR:
      const { filterActor } = action;
      return {
        ...state,
        filterActor: filterActor,
        filterTags: {
          ...state.filterTags,
          filterActor: filterActor
            ? {
                id: AUDIT_TRAIL_PARAMS_NAMES.actor,
                title: "Actor:",
                text: filterActor,
                value: filterActor,
              }
            : null,
        },
      };
    case SET_FILTER_ACTOR_TYPE:
      const { filterActorType } = action;
      return {
        ...state,
        filterActorType: filterActorType,
        filterTags: {
          ...state.filterTags,
          filterActorType: filterActorType
            ? {
                id: AUDIT_TRAIL_PARAMS_NAMES.actorType,
                title: "Actor Type:",
                text: getFilterActorTypeValue(filterActorType),
                value: filterActorType,
              }
            : null,
        },
      };
    case SET_FILTER_SUBJECT_ACCOUNT:
      const { filterSubjectAccount } = action;
      return {
        ...state,
        filterSubjectAccount: filterSubjectAccount,
        filterTags: {
          ...state.filterTags,
          filterSubjectAccount: filterSubjectAccount
            ? {
                id: AUDIT_TRAIL_PARAMS_NAMES.subjectAccount,
                title: "Subject Account:",
                text: filterSubjectAccount,
                value: filterSubjectAccount,
              }
            : null,
        },
      };
    case SET_FILTER_ACTION:
      const { filterAction } = action;
      return {
        ...state,
        filterAction: filterAction,
        filterTags: {
          ...state.filterTags,
          filterAction: filterAction
            ? {
                id: AUDIT_TRAIL_PARAMS_NAMES.action,
                title: "Action:",
                text: getFilterActionValue(filterAction),
                value: filterAction,
              }
            : null,
        },
      };
    case SET_FILTER_STATUS:
      const { filterStatus } = action;
      return {
        ...state,
        filterStatus: filterStatus,
        filterTags: {
          ...state.filterTags,
          filterStatus: filterStatus
            ? {
                id: AUDIT_TRAIL_PARAMS_NAMES.status,
                title: "Status:",
                text: getFilterStatusValue(filterStatus),
                value: filterStatus,
              }
            : null,
        },
      };
    case SET_FILTER_SUBJECT_SITE:
      const { filterSubjectSite } = action;
      return {
        ...state,
        filterSubjectSite: filterSubjectSite,
        filterTags: {
          ...state.filterTags,
          filterSubjectSite: filterSubjectSite
            ? {
                id: AUDIT_TRAIL_PARAMS_NAMES.subjectSite,
                title: "Subject Site:",
                text: filterSubjectSite,
                value: filterSubjectSite,
              }
            : null,
        },
      };
    case SET_FILTER_RESOURCE:
      const { filterResource } = action;
      return {
        ...state,
        filterResource: filterResource,
        filterTags: {
          ...state.filterTags,
          filterResource: filterResource
            ? {
                id: AUDIT_TRAIL_PARAMS_NAMES.resource,
                title: "Object ID:",
                text: filterResource,
                value: filterResource,
              }
            : null,
        },
      };
    case SET_DICTIONARIES:
      return {
        ...state,
        actorTypes: [{ label: "All", value: "" }, ...action.data.ActorTypes],
        actions: [{ label: "All", value: "" }, ...action.data.EventAction],
        statuses: [
          { label: "All", value: "" },
          ...action.data.EventActionStatus,
        ],
      };
    case SET_AVAILABLE_SITES: {
      const { sites } = action;
      return {
        ...state,
        sites: [
          { label: "All", value: "" },
          ...sites
            .map((s, i) => {
              return {
                value: s.name,
                label: s.name,
              };
            })
            .sort((a, b) => Number(a.value) - Number(b.value)),
        ],
      };
    }
    case RESET_EVENTS:
      return {
        ...state,
        events: [],
      };
    case RESET_FILTERS:
      return {
        ...state,
        filterBefore: null,
        filterAfter: null,
        filterActor: null,
        filterActorType: null,
        filterAction: null,
        filterStatus: null,
        filterSubjectSite: null,
        filterResource: null,
        filterSubjectAccount: null,
        filterTags: {},
      };
    case OVERRIDE_FILTERS:
      const { filters } = action;

      return {
        ...state,
        ...filters,
        filterTags: {
          filterAfter: filters.filterAfter
            ? {
                id: AUDIT_TRAIL_PARAMS_NAMES.after,
                title: "From:",
                text: formatDateTimeFilter(filters.filterAfter),
                value: filters.filterAfter,
              }
            : null,
          filterBefore: filters.filterBefore
            ? {
                id: AUDIT_TRAIL_PARAMS_NAMES.before,
                title: "To:",
                text: formatDateTimeFilter(filters.filterBefore),
                value: filters.filterBefore,
              }
            : null,
          filterActor: filters.filterActor
            ? {
                id: AUDIT_TRAIL_PARAMS_NAMES.actor,
                title: "Actor:",
                text: filters.filterActor,
                value: filters.filterActor,
              }
            : null,
          filterActorType: filters.filterActorType
            ? {
                id: AUDIT_TRAIL_PARAMS_NAMES.actorType,
                title: "Actor Type:",
                text: getFilterActorTypeValue(filters.filterActorType),
                value: filters.filterActorType,
              }
            : null,
          filterSubjectAccount: filters.filterSubjectAccount
            ? {
                id: AUDIT_TRAIL_PARAMS_NAMES.subjectAccount,
                title: "Subject Account:",
                text: filters.filterSubjectAccount,
                value: filters.filterSubjectAccount,
              }
            : null,
          filterSubjectSite: filters.filterSubjectSite
            ? {
                id: AUDIT_TRAIL_PARAMS_NAMES.subjectSite,
                title: "Subject Site:",
                text: filters.filterSubjectSite,
                value: filters.filterSubjectSite,
              }
            : null,
          filterAction: filters.filterAction
            ? {
                id: AUDIT_TRAIL_PARAMS_NAMES.action,
                title: "Action:",
                text: getFilterActionValue(filters.filterAction),
                value: filters.filterAction,
              }
            : null,
          filterStatus: filters.filterStatus
            ? {
                id: AUDIT_TRAIL_PARAMS_NAMES.status,
                title: "Status:",
                text: getFilterStatusValue(filters.filterStatus),
                value: filters.filterStatus,
              }
            : null,
          filterResource: filters.filterResource
            ? {
                id: AUDIT_TRAIL_PARAMS_NAMES.resource,
                title: "Object ID:",
                text: filters.filterResource,
                value: filters.filterResource,
              }
            : null,
        },
      };
    case LOGOUT:
      return initialState;
    default:
      return state;
  }
};

//Mutators
const fetchDictionaries = () => (dispatch) => {
  return axios
    .get(`/api/audit/dictionaries`)
    .then(({ data }) => {
      filterDictionaries(data).then((r) =>
        dispatch({
          type: SET_DICTIONARIES,
          data: r,
        })
      );
    })
    .catch((error) => {
      const defaultMessage = error.response?.data
        ? error.response.data
        : "There was an error while trying to fetch the dictionaries!";

      return handleHttpErrors(error.request?.status, {
        default: defaultMessage,
      });
    });
};

const filterDictionaries = (dictionaries) => {
  const whitespaces = "    ";
  let filterDictionaries = {};
  for (const name in dictionaries) {
    filterDictionaries = {
      ...filterDictionaries,
      [name]: [],
    };
    let categories = [];
    for (const entry in dictionaries[name]) {
      let splitEntry = dictionaries[name][entry].split(":");
      if (splitEntry[1])
        filterDictionaries = {
          ...filterDictionaries,
          [name]: !categories.includes(splitEntry[0])
            ? [
                ...filterDictionaries[name],
                { label: `${splitEntry[0]} (All)`, value: `${splitEntry[0]}:` },
                {
                  label: `${whitespaces.repeat(
                    splitEntry.length
                  )}${splitEntry[1].substring(1)}`,
                  value: dictionaries[name][entry],
                },
              ]
            : [
                ...filterDictionaries[name],

                {
                  label: `${whitespaces.repeat(
                    splitEntry.length >= 3 ? 4 : 2
                  )}${splitEntry[splitEntry.length - 1].substring(1)}`,
                  value: dictionaries[name][entry],
                },
              ],
        };
      else
        filterDictionaries = {
          ...filterDictionaries,
          [name]: [
            ...filterDictionaries[name],
            {
              value: dictionaries[name][entry],
              label: dictionaries[name][entry],
            },
          ],
        };
      if (!categories.includes(splitEntry[0])) categories.push(splitEntry[0]);
      //Check if category exists based on size of split, if not exists add it to the array before the first ocurrence where it appears
      if (splitEntry.length >= 3) {
        let splitCat = dictionaries[name][entry].split(":", 3);
        let newCat = `${splitCat[0]}: ${splitCat[1].substring(1)}:`;
        let foundAt = filterDictionaries.EventAction.findIndex(
          (ea) => ea.value === newCat
        );
        if (foundAt < 0) {
          let firstOcurrence = filterDictionaries.EventAction.findIndex(
            (ea) => ea.value === `${dictionaries[name][entry]}`
          );
          if (firstOcurrence > 0) {
            filterDictionaries.EventAction.splice(firstOcurrence, 0, {
              label: `${whitespaces.repeat(
                splitEntry.length - 1
              )}${splitCat[1].substring(1)}`,
              value: newCat,
            });
          }
        }
      }
    }
  }
  return Promise.resolve(filterDictionaries);
};

const formatDateTimeFilter = (dateTimeFilter) => {
  return `${moment(dateTimeFilter).format(DateFormat.DATE_TIME)}`;
};

const getFilterActorTypeValue = (filterActorType) => {
  if (filterActorType.value && filterActorType.value !== "Actor Types (All)") {
    return filterActorType.value;
  } else if (filterActorType !== "Actor Types (All)") {
    return filterActorType;
  }

  return null;
};

const getFilterActionValue = (filterAction) => {
  if (filterAction.value && filterAction.value !== "Actions (All)") {
    return filterAction.value;
  } else if (filterAction !== "Actions (All)") {
    return filterAction;
  }

  return null;
};

const getFilterStatusValue = (filterStatus) => {
  if (filterStatus.value && filterStatus.value !== "Statuses (All)") {
    return filterStatus.value;
  } else if (filterStatus !== "Statuses (All)") {
    return filterStatus;
  }

  return null;
};

const getParams = (getState) => {
  const params = {};

  const filterBefore = getFilterBefore(getState());
  if (filterBefore !== null) {
    params.filterBefore = formatDateTimeFilter(filterBefore);
  }

  const filterAfter = getFilterAfter(getState());
  if (filterAfter !== null) {
    params.filterAfter = formatDateTimeFilter(filterAfter);
  }

  const filterActor = getFilterActor(getState());
  if (filterActor !== null && filterActor !== "") {
    params.filterActor = filterActor;
  }

  const filterActorType = getFilterActorType(getState());
  if (filterActorType !== null) {
    params.filterActorType = getFilterActorTypeValue(filterActorType);
  }

  const filterSubjectAccount = getFilterSubjectAccount(getState());
  if (filterSubjectAccount !== null && filterSubjectAccount !== "") {
    params.filterSubjectAccount = filterSubjectAccount;
  }

  const filterAction = getFilterAction(getState());
  if (filterAction !== null) {
    params.filterAction = getFilterActionValue(filterAction);
  }

  const filterStatus = getFilterStatus(getState());
  if (filterStatus !== null) {
    params.filterStatus = getFilterStatusValue(filterStatus);
  }

  const filterSubjectSite = getFilterSubjectSite(getState());
  if (filterSubjectSite !== null && filterSubjectSite !== "") {
    params.filterSubjectSite = filterSubjectSite;
  }

  const filterResource = getFilterResource(getState());
  if (filterResource !== null && filterResource !== "") {
    params.filterResource = filterResource;
  }

  return params;
};

const fetchAuditEvents = (last) => (dispatch, getState) => {
  const params = getParams(getState);
  if (last) params.filterBefore = last.happenedAt;

  return axios
    .get(`/api/audit/event`, {
      params: {
        Before: params.filterBefore,
        After: params.filterAfter,
        Actor: params.filterActor,
        ActorType: params.filterActorType,
        Action: params.filterAction,
        Status: params.filterStatus,
        SubjectSite: params.filterSubjectSite,
        Resource: params.filterResource,
        Subject: params.filterSubjectAccount,
      },
    })
    .then(({ data }) => {
      dispatch({
        type: SET_EVENTS,
        data,
      });
      return Promise.resolve(data.length);
    })
    .catch((error) => {
      const defaultMessage = error.response?.data
        ? error.response.data
        : "There was an error while trying to fetch the Audit events!";

      return handleHttpErrors(error.request?.status, {
        default: defaultMessage,
      });
    });
};

export const resetEvents = () => (dispatch) => {
  dispatch({ type: RESET_EVENTS });
};

const resetFilters = () => (dispatch) => {
  dispatch({ type: RESET_FILTERS });
};

const fetchAuditEventsByFilters = (filters) => (dispatch) => {
  dispatch({ type: RESET_EVENTS });
  dispatch({
    type: OVERRIDE_FILTERS,
    filters: filters,
  });

  return axios
    .get(`/api/audit/event`, {
      params: {
        Before: filters?.filterBefore
          ? formatDateTimeFilter(filters.filterBefore)
          : null,
        After: filters?.filterAfter
          ? formatDateTimeFilter(filters.filterAfter)
          : null,
        Actor: filters?.filterActor || null,
        ActorType: filters?.filterActorType || null,
        Action: filters?.filterAction || null,
        Status: filters?.filterStatus || null,
        SubjectSite: filters?.filterSubjectSite || null,
        Resource: filters?.filterResource || null,
        Subject: filters?.filterSubjectAccount || null,
      },
    })
    .then(({ data }) => {
      dispatch({
        type: SET_EVENTS,
        data,
      });
      return Promise.resolve(data.length);
    })
    .catch((error) => {
      const defaultMessage = error.response?.data
        ? error.response.data
        : "There was an error while trying to fetch the Audit events!";

      return handleHttpErrors(error.request?.status, {
        default: defaultMessage,
      });
    });
};

const fetchSites = () => (dispatch, getState) => {
  return axios
    .get("/api/study/sites", {
      params: { studyId: studiesIds.lcfaod302 },
    })
    .then(({ data }) => dispatch({ type: SET_AVAILABLE_SITES, sites: data }))
    .catch((error) => {
      return handleHttpErrors(error.request?.status);
    });
};

export const eventReset = () => {
  try {
    resetFilters();
  } catch (e) {
    return Promise.reject(e);
  }
};

export const updateEvents = (events) => (dispatch) => {
  dispatch({ type: UPDATE_EVENTS, events: events });
};

export const toggleEventsData = (open) => (dispatch, getState) => {
  const events = getEvents(getState()).map((event) => {
    return {
      ...event,
      open: open,
    };
  });

  dispatch({ type: UPDATE_EVENTS, events: events });
};

export const mapStateToProps = (state) => {
  return {
    events: getEvents(state),
    filterBefore: getFilterBefore(state),
    filterAfter: getFilterAfter(state),
    filterActor: getFilterActor(state),
    filterActorType: getFilterActorType(state),
    filterSubjectAccount: getFilterSubjectAccount(state),
    filterAction: getFilterAction(state),
    filterStatus: getFilterStatus(state),
    filterSubjectSite: getFilterSubjectSite(state),
    filterResource: getFilterResource(state),
    actorTypes: getActorTypes(state),
    actions: getActions(state),
    statuses: getStatuses(state),
    user_sites: state?.loginReducer.sites,
    sites: getSites(state),
    filterTags: getFilterTags(state),
  };
};

export const mapDispatchToProps = {
  fetchDictionaries,
  fetchAuditEvents,
  resetEvents,
  resetFilters,
  fetchAuditEventsByFilters,
  fetchSites,
  updateEvents,
  toggleEventsData,
};
